/**
 * A map's link (docs/plans/2026-10-28-link-sharing.md): what the app computes
 * from its secret, and how the link is written.
 *
 * The secret (32 random bytes) travels after '#' in the link, a part browsers
 * never send to a server. From it, with HKDF-SHA-256:
 *  - the link's X25519 key pair: its entry in the map's key log, so the map
 *    key is sealed for it there like for any member (key-authority.js);
 *  - a token: the server keeps only its hash, to know who holds the link.
 * The link also names the public key of who made it (the owner or an admin):
 * the joining app requires the link's entry in the key log to come from that
 * key. The server cannot forge it (it holds neither that key nor the link's
 * secret), so it cannot present another map, or a member list of its own
 * (an invented admin), under the link. (Someone else holding the link, with
 * the server's help, could: a box can be forged by its recipient, and all
 * who hold a link are that recipient; signed key-log events would close it.)
 *
 * Link: <server>/join#<link id>.<secret>.<creator key>, each part base64url
 * without padding (22, 43 and 43 characters).
 *
 * The secret is also kept on the server encrypted under the map key, for the
 * owner and admins to copy the link again: base64(iv[12] || AES-256-GCM(map
 * key, secret, aad)), the aad binding it to the map and the link.
 */
import nacl from 'tweetnacl';
import { Keypair } from './keypair.js';

const encoder = new TextEncoder();

function toBase64Url(bytes) {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(text) {
  if (!/^[A-Za-z0-9_-]*$/.test(text)) throw new Error('Invalid link');
  const plain = text.replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(plain + '='.repeat((4 - (plain.length % 4)) % 4)), c => c.charCodeAt(0));
}

const uuidToBytes = id => Uint8Array.from(id.replace(/-/g, '').match(/../g).map(h => parseInt(h, 16)));

function bytesToUuid(bytes) {
  const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

async function derive(secret, info) {
  const base = await crypto.subtle.importKey('raw', secret, 'HKDF', false, ['deriveBits']);
  return new Uint8Array(await crypto.subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(32), info: encoder.encode(info) }, base, 256));
}

/** A new link's secret. */
export function newLinkSecret() {
  return nacl.randomBytes(32);
}

/**
 * The link's key pair (its identity in the map's key log) and the token the
 * server checks.
 * @param {Uint8Array} secret
 * @returns {Promise<{keypair: {publicKey: Uint8Array, secretKey: Uint8Array}, token: Uint8Array}>}
 */
export async function linkKeys(secret) {
  if (!(secret instanceof Uint8Array) || secret.length !== 32) throw new Error('Invalid link');
  const seed = await derive(secret, 'orbital link v1 key');
  try {
    const keypair = nacl.box.keyPair.fromSecretKey(seed);
    return { keypair: { publicKey: keypair.publicKey, secretKey: keypair.secretKey }, token: await derive(secret, 'orbital link v1 token') };
  } finally {
    seed.fill(0);
  }
}

/**
 * @param {string} origin - the server's (not the desktop app's)
 * @param {string} linkId
 * @param {Uint8Array} secret
 * @param {string} creatorPublicKey - base64, the key of who made the link (its entry's author in the key log)
 */
export function linkAddress(origin, linkId, secret, creatorPublicKey) {
  return `${origin.replace(/\/+$/, '')}/join#${toBase64Url(uuidToBytes(linkId))}.${toBase64Url(secret)}.${toBase64Url(Keypair.fromBase64(creatorPublicKey))}`;
}

/**
 * Read a link (the whole address, or only what follows '#').
 * @returns {{linkId: string, secret: Uint8Array, creatorPublicKey: string}|null}
 */
export function parseLinkAddress(address) {
  const text = String(address || '');
  const parts = (text.includes('#') ? text.slice(text.indexOf('#') + 1) : text).trim().split('.');
  if (parts.length !== 3) return null;
  try {
    const id = fromBase64Url(parts[0]);
    const secret = fromBase64Url(parts[1]);
    const owner = fromBase64Url(parts[2]);
    if (id.length !== 16 || secret.length !== 32 || owner.length !== 32) return null;
    return { linkId: bytesToUuid(id), secret, creatorPublicKey: Keypair.toBase64(owner) };
  } catch {
    return null;
  }
}

const secretAad = (docId, linkId) => encoder.encode(`orbital:v1:link-secret:${docId}:${linkId}`);

async function aesKey(dek, usage) {
  if (!(dek instanceof Uint8Array) || dek.length !== 32) throw new Error('Invalid map key');
  return crypto.subtle.importKey('raw', dek, { name: 'AES-GCM', length: 256 }, false, [usage]);
}

/** The secret encrypted under the map key (kept by the server for owner and admins). */
export async function encryptLinkSecret(dek, docId, linkId, secret) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: secretAad(docId, linkId) }, await aesKey(dek, 'encrypt'), secret));
  const out = new Uint8Array(iv.length + ciphertext.length);
  out.set(iv);
  out.set(ciphertext, iv.length);
  return Keypair.toBase64(out);
}

/** @returns {Promise<Uint8Array>} the secret; throws for another map, link or key */
export async function decryptLinkSecret(dek, docId, linkId, encrypted) {
  const bytes = Keypair.fromBase64(encrypted);
  if (bytes.length < 29) throw new Error('Invalid encrypted link');
  const secret = new Uint8Array(await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: bytes.subarray(0, 12), additionalData: secretAad(docId, linkId) },
    await aesKey(dek, 'decrypt'), bytes.subarray(12)));
  if (secret.length !== 32) throw new Error('Invalid encrypted link');
  return secret;
}
